import { useState, type ComponentProps } from 'react';
import { createStyles } from '@mantine/core';

import { BarChart } from '~/domains/design-system/components/charts/BarChart/BarChart';
import { Text } from '~/domains/design-system/Text';
import { SegmentedControl } from '~/domains/common/components/SegmentedControl/SegmentedControl';

import type { PlayerGroupedStats, PlayerInfo, PlayerInfoAfterGameStart } from '../../types/player';

import GamecenterPlayerDetailGameInfo from './GamecenterPlayerDetailGameInfo';
import GamecenterPlayerDetailScoreBreakdown from './GamecenterPlayerDetailScoreBreakdown';
import GamecenterPlayerDetailStats from './GamecenterPlayerDetailStats';
import GamecenterPlayerDetailGameLog from './GamecenterPlayerDetailGameLog';
import type { GamecenterPlayerDetailActionProps } from './GamecenterPlayerDetailActions/GamecenterPlayerDetailAction';
import GamecenterPlayerDetailActions from './GamecenterPlayerDetailActions/GamecenterPlayerDetailActions';
import { toSentenceCase } from '~/domains/contest/domains/detail/utils/deadlineFormat';

const useStyles = createStyles((theme) => ({
  container: {
    height: '100%',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.other.spacing._16,
    paddingLeft: theme.other.spacing._16,
    paddingRight: theme.other.spacing._16,
  },
  chartWrapper: {
    backgroundColor: theme.colors.gray2[0],
    paddingBottom: theme.other.spacing._16,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.other.spacing._16,
  },
  chartStats: {
    marginLeft: theme.other.spacing._16,
  },
}));

type GamecenterPlayerDetailContentProps = {
  playerInfo: PlayerInfo;
  groupedStats: PlayerGroupedStats;
  gameLogProps: ComponentProps<typeof GamecenterPlayerDetailGameLog>;
  actions: GamecenterPlayerDetailActionProps[];
  propTypeDisplay: string;
  type?: 'survivor-prop' | 'others';
};

function GamecenterPlayerDetailContent({
  playerInfo,
  groupedStats,
  gameLogProps,
  actions,
  type = 'others',
  propTypeDisplay,
}: GamecenterPlayerDetailContentProps) {
  const { classes, theme } = useStyles();
  const [activeTab, setActiveTab] = useState<string>('recent');

  return (
    <div className={classes.container}>
      <div className={classes.content}>
        {type === 'survivor-prop' && (
          <SegmentedControl
            data={[
              { label: 'Recent', value: 'recent' },
              { label: 'Game log', value: 'gameLog' },
            ]}
            onChange={setActiveTab}
            fullWidth
          />
        )}

        {type === 'others' && <GamecenterPlayerDetailGameInfo playerInfo={playerInfo} />}
        {type === 'others' &&
          (playerInfo as PlayerInfoAfterGameStart)?.scoreBreakdown?.length > 0 && (
            <GamecenterPlayerDetailScoreBreakdown
              tableData={(playerInfo as PlayerInfoAfterGameStart).scoreBreakdown}
            />
          )}
        {type === 'survivor-prop' && <GamecenterPlayerDetailStats groupedStats={groupedStats} />}
        {type === 'survivor-prop' && activeTab === 'gameLog' && (
          <GamecenterPlayerDetailGameLog {...gameLogProps} />
        )}
        {type === 'survivor-prop' &&
        playerInfo?.chartProps &&
        activeTab === 'recent' &&
        playerInfo.chartProps.tickCount ? (
          <>
            <Text variant="headline-large">
              {toSentenceCase(propTypeDisplay)} (Last {playerInfo.chartProps.tickCount} games)
            </Text>
            <div className={classes.chartWrapper}>
              <BarChart type={type} invertColors {...playerInfo.chartProps} />
              <div className={classes.chartStats}>
                <Text color={theme.colors.gray2[3]}>
                  Last {playerInfo.chartProps.tickCount} avg:{' '}
                </Text>
                <Text fw={900}>{playerInfo.chartProps.lastXAvg}</Text>
              </div>
            </div>
          </>
        ) : null}
        {type === 'others' && <GamecenterPlayerDetailStats groupedStats={groupedStats} />}
        {type === 'others' && <GamecenterPlayerDetailGameLog {...gameLogProps} />}
      </div>
      <GamecenterPlayerDetailActions actions={actions} />
    </div>
  );
}

export default GamecenterPlayerDetailContent;
